/**
 * MultiTargetResolver - Multi-Element Selection
 *
 * Resolves user references to one or multiple elements on screen.
 * Handles "all text", "both images", "everything except the title", etc.
 */

import { RichElement, RichScreenContext, SemanticRole } from './ScreenAnalyzer';

export type SelectionMode = 'single' | 'multi' | 'all_of_type' | 'all' | 'except';

export interface TargetSelector {
  mode: SelectionMode;
  typeFilter?: string[];
  excludeHint?: string;
  relativeRef?: 'biggest' | 'smallest' | 'first' | 'last';
  roleFilter?: SemanticRole[];
}

export interface ResolvedTargets {
  elements: RichElement[];
  selector: TargetSelector;
  confidence: number;
  disambiguation?: string;
}

/**
 * Parse a target hint string into a structured selector
 */
export function parseTargetSelector(targetHint: string): TargetSelector {
  const hint = targetHint.toLowerCase().trim();

  // "everything except ..."
  const exceptMatch = hint.match(/^everything\s+except\s+(.+)$/);
  if (exceptMatch) {
    return { mode: 'except', excludeHint: exceptMatch[1] };
  }

  // "everything" / "all elements"
  if (/^(everything|all\s+elements?|all\s+of\s+them)$/.test(hint)) {
    return { mode: 'all' };
  }

  // "all text" / "all the text" / "every text"
  if (/^(all|every|each)\s+(the\s+)?text/.test(hint)) {
    return { mode: 'all_of_type', typeFilter: ['text'] };
  }

  // "all images"
  if (/^(all|every|each|both)\s+(the\s+)?image/.test(hint)) {
    return { mode: 'all_of_type', typeFilter: ['image'] };
  }

  // "all shapes"
  if (/^(all|every|each|both)\s+(the\s+)?shape/.test(hint)) {
    return { mode: 'all_of_type', typeFilter: ['shape'] };
  }

  // "both images"
  if (/^both\s+/.test(hint)) {
    const typeMatch = hint.match(/^both\s+(text|image|shape|video)s?$/);
    if (typeMatch) {
      return { mode: 'all_of_type', typeFilter: [typeMatch[1]] };
    }
    return { mode: 'all' };
  }

  // "all" (without specifying what)
  if (hint === 'all') {
    return { mode: 'all' };
  }

  // "the bigger one" / "the largest"
  if (/\b(bigger|largest|biggest)\b/.test(hint)) {
    return { mode: 'single', relativeRef: 'biggest' };
  }
  if (/\b(smaller|smallest|tiniest)\b/.test(hint)) {
    return { mode: 'single', relativeRef: 'smallest' };
  }

  // Role-based selectors
  if (/^title$/.test(hint)) {
    return { mode: 'single', roleFilter: ['title'] };
  }
  if (/^subtitle$/.test(hint)) {
    return { mode: 'single', roleFilter: ['subtitle'] };
  }
  if (/^body$/.test(hint)) {
    return { mode: 'single', roleFilter: ['body'] };
  }

  // Default to single target
  return { mode: 'single' };
}

/**
 * Resolve a target hint to one or more elements from the screen context
 */
export function resolveTargets(
  targetHint: string,
  context: RichScreenContext
): ResolvedTargets {
  const selector = parseTargetSelector(targetHint);
  const elements = context.elements;

  switch (selector.mode) {
    case 'all':
      return {
        elements: [...elements],
        selector,
        confidence: 0.95,
      };

    case 'all_of_type': {
      const filtered = elements.filter((e) =>
        selector.typeFilter!.includes(e.type)
      );
      if (filtered.length === 0) {
        return {
          elements: [],
          selector,
          confidence: 0,
          disambiguation: `No ${selector.typeFilter!.join('/')} elements found on this screen.`,
        };
      }
      return {
        elements: filtered,
        selector,
        confidence: 0.9,
      };
    }

    case 'except': {
      const excludeSelector = parseTargetSelector(selector.excludeHint || '');
      const excluded = resolveSingleOrRole(excludeSelector, elements);
      const excludedIds = new Set(excluded.map((e) => e.mmoId));
      const remaining = elements.filter((e) => !excludedIds.has(e.mmoId));
      return {
        elements: remaining,
        selector,
        confidence: 0.85,
      };
    }

    case 'multi': {
      if (selector.typeFilter) {
        const filtered = elements.filter((e) =>
          selector.typeFilter!.includes(e.type)
        );
        return {
          elements: filtered,
          selector,
          confidence: 0.85,
        };
      }
      return {
        elements: [...elements],
        selector,
        confidence: 0.7,
      };
    }

    case 'single':
    default: {
      const matches = resolveSingleOrRole(selector, elements);
      if (matches.length === 0) {
        // Fall back to type-based matching from the hint
        const typeMatch = resolveByTypeHint(targetHint, elements);
        if (typeMatch.length > 0) {
          return {
            elements: [typeMatch[0]],
            selector,
            confidence: 0.7,
          };
        }
        return {
          elements: [],
          selector,
          confidence: 0,
          disambiguation: `Could not find element matching "${targetHint}".`,
        };
      }
      return {
        elements: [matches[0]],
        selector,
        confidence: matches.length === 1 ? 0.9 : 0.75,
        disambiguation:
          matches.length > 1
            ? `Multiple matches found for "${targetHint}", using the most likely one.`
            : undefined,
      };
    }
  }
}

/**
 * Resolve to elements by role or relative reference
 */
function resolveSingleOrRole(
  selector: TargetSelector,
  elements: RichElement[]
): RichElement[] {
  // Relative reference
  if (selector.relativeRef) {
    const sorted = [...elements];
    switch (selector.relativeRef) {
      case 'biggest':
        sorted.sort((a, b) => b.area - a.area);
        break;
      case 'smallest':
        sorted.sort((a, b) => a.area - b.area);
        break;
      case 'first':
        return sorted.length > 0 ? [sorted[0]] : [];
      case 'last':
        return sorted.length > 0 ? [sorted[sorted.length - 1]] : [];
    }
    return sorted.length > 0 ? [sorted[0]] : [];
  }

  // Role filter
  if (selector.roleFilter) {
    return elements.filter((e) =>
      selector.roleFilter!.includes(e.semanticRole)
    );
  }

  return [];
}

/**
 * Resolve by type hint string (e.g., "image", "text", "shape")
 */
function resolveByTypeHint(hint: string, elements: RichElement[]): RichElement[] {
  const h = hint.toLowerCase();

  if (h.includes('image') || h.includes('picture') || h.includes('photo')) {
    return elements.filter((e) => e.type === 'image');
  }
  if (h.includes('video')) {
    return elements.filter((e) => e.type === 'video');
  }
  if (h.includes('shape') || h.includes('rectangle') || h.includes('box') || h.includes('circle')) {
    return elements.filter((e) => e.type === 'shape');
  }
  if (h.includes('text') || h.includes('title') || h.includes('subtitle') || h.includes('heading')) {
    const textElements = elements.filter((e) => e.type === 'text');
    if (h.includes('title')) {
      const titles = textElements.filter((e) => e.semanticRole === 'title');
      if (titles.length > 0) return titles;
    }
    if (h.includes('subtitle')) {
      const subtitles = textElements.filter((e) => e.semanticRole === 'subtitle');
      if (subtitles.length > 0) return subtitles;
    }
    return textElements;
  }
  if (h === 'it' || h === 'background') {
    // "it" typically refers to the most prominent element
    const sorted = [...elements].sort((a, b) => b.area - a.area);
    return sorted.length > 0 ? [sorted[0]] : [];
  }

  return [];
}
